/**
 * FLOYD 2026-10-26 (DEFECT 2) — THE STARTED-WORK CARRY WINDOW, ONE ANSWER FOR BROWSER AND SERVER.
 *
 * Carrying started work past its end of day needs three facts about time:
 *
 *   1. where the work's OWN day ends (Part A's boundary) — the technician's DATED hours for the ticket's
 *      scheduled shop-local date, net of breaks and partial absences;
 *   2. how much work remains (the technician's word, else OptiTime minus what that day held); and
 *   3. where the continuation can start — never on a day that has already ended.
 *
 * `createEODRolloverSplit` (create-eod-rollover) has always answered them on the server. The browser preflight in
 * EODRolloverModal answered them AGAIN, differently: it parsed an "HH:mm" string out of whatever the opener passed as
 * `techEOD` (the weekly flat `shift_end`, or — from the decision panel — `String(detection.boundary_at ?? '')`, an
 * ISO instant or ''), applied it to TODAY with browser-local `setHours`, and invented the next window as now + 16 h.
 * On 2026-10-26 the panel's '' never matched, `shiftEnd` stayed undefined, and every "Approve rollover" of started
 * work was refused for `missing_shift_window` before the server was asked.
 *
 * Both sides now compute these facts HERE, from the same inputs, with the same planner, so the preflight and the
 * authority cannot disagree about today's or the continuation day's working window. Pure: no I/O, no clock.
 */

import { parseTimeToMinutes } from './canonicalTimeMath.ts';
import { makeShopWindowInstants, makeUTCFromShopMinutes } from './timezoneMath.ts';
import {
  lastWorkingMinute,
  nextCalendarDate,
  planRolloverSegments,
  workCapacityFrom,
  type PlannerDay,
  type ResolveWorkDay,
  type RolloverPlan,
} from './rolloverSegmentPlanner.ts';

/**
 * The shop's IANA time zone for a carry, or null when it is not known. A carry is planned in shop-local days and
 * minutes; planning it in a GUESSED zone (the old `|| 'America/New_York'`) silently moves every boundary by hours.
 * Unknown → the carry is refused (SHOP_TIMEZONE_UNKNOWN), never planned in another shop's day.
 */
export function resolveCarryShopTimezone(timeZone: unknown): string | null {
  if (typeof timeZone !== 'string' || timeZone.trim() === '') return null;
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone: timeZone.trim() }).resolvedOptions().timeZone;
  } catch {
    return null; // not a real IANA zone
  }
}

export interface PartAWindow {
  /** 'dated' = the technician's hours for that date; 'flat_shift_end' = the weekly column (no dated answer). */
  source: 'dated' | 'flat_shift_end';
  /** Shop-local minute at which the work's own day ends (Part A's boundary). NaN when no shift is known at all. */
  boundaryMin: number;
  /** Working minutes that day held from the work's start minute. */
  todayMinutes: number;
}

/**
 * Part A's boundary and the minutes that day held — `createEODRolloverSplit`'s rule, verbatim: the dated
 * normal-hours boundary when the day resolves; only without a dated answer does the flat `shift_end` speak, with
 * its own break arithmetic. A flat value that does not parse leaves `boundaryMin` NaN: no shift is known.
 */
export function resolvePartAWindow(input: {
  today: PlannerDay | null;
  scheduledDate: string;
  startMin: number;
  flatShiftEnd: string | null | undefined;
  recurringBreaks?: unknown;
  nonRecurringBreaks?: unknown;
}): PartAWindow {
  const { today, scheduledDate, startMin } = input;
  if (today) {
    return { source: 'dated', boundaryMin: lastWorkingMinute(today), todayMinutes: workCapacityFrom(today, startMin) };
  }
  // deno-lint-ignore no-explicit-any
  const asBreaks = (raw: unknown): any[] => (Array.isArray(raw) ? raw : []);
  const boundaryMin = parseTimeToMinutes(String(input.flatShiftEnd ?? ''));
  const breaks = [
    // deno-lint-ignore no-explicit-any
    ...asBreaks(input.recurringBreaks).map((b: any) => ({ start: parseTimeToMinutes(b.start), end: parseTimeToMinutes(b.end) })),
    ...asBreaks(input.nonRecurringBreaks)
      // deno-lint-ignore no-explicit-any
      .filter((b: any) => b.date === scheduledDate)
      // deno-lint-ignore no-explicit-any
      .map((b: any) => ({ start: parseTimeToMinutes(b.start), end: parseTimeToMinutes(b.end) })),
  ];
  const breakMinutesInWindow = breaks
    .reduce((sum, b) => sum + Math.max(0, Math.min(b.end, boundaryMin) - Math.max(b.start, startMin)), 0);
  return { source: 'flat_shift_end', boundaryMin, todayMinutes: Math.max(0, boundaryMin - startMin - breakMinutesInWindow) };
}

/**
 * The first date a continuation may occupy: the day after the work's own day — or, when that day is already
 * past (a stale day decided later), the shop's today. Never a day that has already ended (OCT20 WS-ROLL FD-11).
 */
export function continuationStartDate(scheduledDate: string, shopToday: string): string {
  const next = nextCalendarDate(scheduledDate);
  return next < shopToday ? shopToday : next;
}

export interface StartedWorkCarrySegment {
  dateStr: string;
  startMin: number;
  finishMin: number;
  workMinutes: number;
  startAt: Date;
  finishAt: Date;
}

export type StartedWorkCarryWindow =
  /** No shift is known for the work's own day — genuinely missing context; nothing is invented. */
  | { state: 'no_shift_window'; partA: PartAWindow }
  /** The work's day is known, but neither the technician nor the estimate says how much remains. */
  | { state: 'no_remaining_time'; partA: PartAWindow; shiftEndAt: Date; systemEstimatedRemainingMinutes: number }
  /** The remainder does not fit any valid workday within the planner's horizon. */
  | { state: 'no_valid_next_window'; partA: PartAWindow; shiftEndAt: Date; systemEstimatedRemainingMinutes: number;
      remainingMinutes: number; plan: RolloverPlan }
  | { state: 'ready'; partA: PartAWindow; shiftEndAt: Date; systemEstimatedRemainingMinutes: number;
      remainingMinutes: number; plan: RolloverPlan; segments: StartedWorkCarrySegment[];
      /** The continuation's first instant — the planner's next valid window. */
      nextWindowStartAt: Date;
      /** When the whole carry would finish (what a due-out promise is measured against). */
      finalFinishAt: Date };

/**
 * Everything the server will decide about time, decided first. `remainingMinutesOverride` is the technician's
 * stated remainder (outranks the estimate, as on the server); `optiTime` is the ticket's OptiTime in work minutes.
 */
export function planStartedWorkCarry(input: {
  scheduledDate: string;
  shopToday: string;
  shopTimezone: string;
  resolveDay: ResolveWorkDay;
  startMin: number;
  optiTime: number;
  flatShiftEnd: string | null | undefined;
  recurringBreaks?: unknown;
  nonRecurringBreaks?: unknown;
  remainingMinutesOverride?: number | null;
}): StartedWorkCarryWindow {
  const partA = resolvePartAWindow({
    today: input.resolveDay(input.scheduledDate),
    scheduledDate: input.scheduledDate,
    startMin: input.startMin,
    flatShiftEnd: input.flatShiftEnd,
    recurringBreaks: input.recurringBreaks,
    nonRecurringBreaks: input.nonRecurringBreaks,
  });
  if (!Number.isFinite(partA.boundaryMin)) return { state: 'no_shift_window', partA };

  const shiftEndAt = makeUTCFromShopMinutes(input.scheduledDate, partA.boundaryMin, input.shopTimezone);
  const systemEstimatedRemainingMinutes = input.optiTime - partA.todayMinutes;
  const override = input.remainingMinutesOverride;
  const resolved = typeof override === 'number' && Number.isFinite(override) && override > 0
    ? override
    : systemEstimatedRemainingMinutes;
  if (!(typeof resolved === 'number' && Number.isFinite(resolved) && resolved > 0)) {
    return { state: 'no_remaining_time', partA, shiftEndAt, systemEstimatedRemainingMinutes };
  }
  const remainingMinutes = Math.max(1, Math.round(resolved));
  const plan = planRolloverSegments({
    totalWorkMinutes: remainingMinutes,
    startDateStr: continuationStartDate(input.scheduledDate, input.shopToday),
    startMin: 0,
    resolveDay: input.resolveDay,
  });
  if (!plan.complete || plan.segments.length === 0) {
    return { state: 'no_valid_next_window', partA, shiftEndAt, systemEstimatedRemainingMinutes, remainingMinutes, plan };
  }
  const segments = plan.segments.map((seg) => {
    const w = makeShopWindowInstants(seg.dateStr, seg.startMin, seg.finishMin, input.shopTimezone);
    return { dateStr: seg.dateStr, startMin: seg.startMin, finishMin: seg.finishMin, workMinutes: seg.workMinutes, startAt: w.start, finishAt: w.finish };
  });
  return {
    state: 'ready', partA, shiftEndAt, systemEstimatedRemainingMinutes, remainingMinutes, plan, segments,
    nextWindowStartAt: segments[0].startAt,
    finalFinishAt: segments[segments.length - 1].finishAt,
  };
}
